import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Formik, Form, FieldArray, FormikProps } from "formik";

import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "../../../../framework/redux/hooks";
import { handleApiError } from "../../../../services/baseService";

import Grid2 from "@mui/material/Unstable_Grid2";
import { ProductRows } from "./productRows";
import { TransportOrderProductDetails } from "../../../../models/transport/transportOrderProductDetails";
import { validationSchema, productTableSx, handleProductRequest } from "./productUtils";
import { getOwnerProducts, selectProducts } from "../../../../framework/redux/slices/productSlice";
import { ProductItem, TransportOrderProductItem } from "../../../../models/product/product";
import { TransportOrderDetailsDto } from "../../../../models/transport/transportOrder";
import { updateEditingOrder } from "../../../../framework/redux/slices/transportOrderDetailsSlice";
import { addErrorMsg, addSuccessMsg } from "../../../../framework/redux/slices/appSlice";

export interface ProductForm {
    rows: TransportOrderProductDetails[];
}

export interface ProductTableProps {
    order?: TransportOrderDetailsDto;
    changesDisabled?: boolean;
}

export const ProductTable = ({ changesDisabled } : ProductTableProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const order = useAppSelector(state => state.transportOrderDetails.editingOrder);
    const [orderAddedProducts, setOrderAddedProducts] = useState<string[]>();

    const products = useSelector(selectProducts);
    const initialValues: ProductForm = {
        rows: order.products?.map((p) => ({
            id: null,
            key: crypto.randomUUID(),
            transportOrderId: order.id,
            productId: p.id,
            amount: p.quantity
        }))
    };

    useEffect(() => {
        const promise = dispatch(getOwnerProducts());
        promise.unwrap().catch(handleApiError);
        return () => {
            promise.abort();
        };
    }, []);

    useEffect(() => {
        const products = [];
        order.products.forEach(p => {
            products.push(p.id);
        });
        setOrderAddedProducts(products);
    }, [order]);

    const handleSubmit = async(formData: ProductForm) => {
        if (!changesDisabled) {
            dispatch(updateEditingOrder({
                products: formData.rows.map(row => ({
                    id: row.productId,
                    quantity: row.amount
                }))
            }));
            const productRows: TransportOrderProductItem[] = [];
            formData.rows.forEach(p => {
                const product: TransportOrderProductItem = {
                    id: p.productId,
                    quantity: p.amount,
                };
                productRows.push(product);
            });
            try {
                await handleProductRequest(order, productRows, dispatch);
                dispatch(addSuccessMsg(t("transport.order.productSaveSuccess")));
            } catch (error) {
                dispatch(addErrorMsg({ message: t("transport.order.productSaveFailed") }));
            }
        }
    };

    return (
        <Box ml={2} my={2}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.dark", pb: 2 }}>
                { t("transport.order.products") }
            </Typography>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                { (formikProps: FormikProps<ProductForm>) => ProductForm(formikProps, changesDisabled, products, order, orderAddedProducts) }
            </Formik>
        </Box>
    );
};

const ProductForm = (
    formikProps: FormikProps<ProductForm>,
    changesDisabled: boolean,
    products: ProductItem[],
    order: TransportOrderDetailsDto,
    orderAddedProducts?: string[]
) => {
    const { t } = useTranslation();

    function newEmptryRow() {
        const emptyRow: TransportOrderProductDetails = {
            id: null,
            key: crypto.randomUUID(),
            transportOrderId: null,
            productId: "",
            amount: 1,
            rowId: null,
        };

        return emptyRow;
    }

    return (
        <Form>
            <FieldArray
                name="rows"
                render={(arrayHelpers) => {
                    return (
                        <>
                            <TableContainer>
                                <Table {...productTableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell>{ t("transport.parcel.name") }</TableCell>
                                            <TableCell>{ t("transport.parcel.amount") }</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <ProductRows
                                            formikProps={formikProps}
                                            arrayHelpers={arrayHelpers}
                                            changesDisabled={changesDisabled}
                                            order={order}
                                            ownerProducts={products}
                                            orderAddedProducts={orderAddedProducts}
                                        />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <>
                                { !changesDisabled &&
                                    <Grid2 container>
                                        <Grid2>
                                            <Fab
                                                data-testid={"productTable-add"}
                                                color="primary"
                                                size="small"
                                                onClick={() => arrayHelpers.push(newEmptryRow())}
                                            >
                                                <AddIcon />
                                            </Fab>
                                        </Grid2>
                                    </Grid2>
                                }
                            </>
                        </>
                    );
                }}
            />
        </Form>
    );
};
